/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { CimaCore, Cloudinary, Util, XmStore, XmStoreUtil } from 'core/services';
import { StateService } from '@uirouter/core';
import { MyAccountModal } from 'my-account/services/modal';
import { CmsAddonPlans, CmsMyAccountModals } from 'store/cms/models';
import { Addon } from 'store/addons';
import { User } from 'store/user/models';
import { AddonAction, AddonRouteType, AddonType } from 'my-account/constants';
import { AccountSummary, LineSummary } from 'store/account-summary/models';
import { XmPayAsYouGoModal } from 'my-account/shared/modals/pay-as-you-go/pay-as-you-go';
import { AddonLines, AddOnPassOption, AddRemovePassAction } from 'my-account/interface';
import { AddonsUtil } from 'my-account/activity/line-detail/addon/services/addons-util';
import { GlobalTravelPass, GlobalTravelPassLine, GlobalTravelPassTab } from 'store/global-travel-pass/models';
import { StoreAction } from 'store/actions';
import { CONFIG_TOKEN } from 'core/constants';
import { IXMOptions } from 'core/interfaces';

@Component({
    selector: 'xm-addon-review',
    templateUrl: './addon-review.html',
    styleUrls: ['./addon-review.scss'],
    standalone: false
})

export class XmAddonReview implements OnInit, OnDestroy {

    public planData: CmsAddonPlan;
    public pageData: CmsReviewData;
    public addonData: Addon;
    public headerLinkParams: object;
    public imagePrimary: MediaImageOptions;
    public features: ReviewFeatures[] = [];
    public imageIconTimeInfo: MediaImageOptions;
    public lines: LineSummary[];
    public gtpSupportLinks: CmsGtpSupportLink[];
    public mexCanSupportLinks: string[];
    public isGTP: boolean = false;
    public isMexCan: boolean = false;
    public intent: string;
    public enableNextButton: boolean = false;
    public addonLines: AddonLines = {};
    public isLoading: boolean = true;
    public isBusinessUser: boolean = false;
    public isSeg6: boolean = false;
    public enableCbmNoLevel: boolean = false;
    public isCBMInternationalReteLinkEnabled: boolean = false;
    public cimaCore: CimaCore;

    private addonType: string;
    private headerLinkAddonType: string;
    private xmStore: XmStore;
    private state: StateService;
    private subscriptions: Subscription[] = [];
    private accountModal: MyAccountModal;
    private payAsYouGoModalData: CmsPayAsYouGoModal;
    private addonsUtil: AddonsUtil;
    private config: IXMOptions;

    constructor(accountModal: MyAccountModal, xmStore: XmStore, state: StateService, addonsUtil: AddonsUtil, @Inject(CONFIG_TOKEN) config: IXMOptions, cimaCore: CimaCore) {
        Object.assign(this, { accountModal, xmStore, state, addonsUtil, config, cimaCore });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.isSeg6 = user.account.isSegmentSix;
            this.enableCbmNoLevel = this.config.ENABLE_CBM_GEMINI && this.isBusinessUser && user.account.isNoLevel;
        }));

        this.isCBMInternationalReteLinkEnabled = this.config.ENABLE_CBM_INTERNATIONAL_RETE_LINK;
       
        if (this.addonsUtil.addonsSwitchTimeout) {
            this.addonsUtil.completeAddonsPolling();
        }
        this.intent = this.state.params.intent;
        
        if (this.state.params.addon_type === AddonRouteType.GTP) {
            this.isGTP = true;
            this.isMexCan = false;
            this.addonType = AddonType.GTP;
            this.headerLinkAddonType = AddonRouteType.MEX_CAN_PASS;
        } else if (this.state.params.addon_type === AddonRouteType.MEX_CAN_PASS) {
            this.isGTP = false;
            this.isMexCan = true;
            this.addonType = AddonType.CAN_MEX_UNL_PASS;
            this.headerLinkAddonType = AddonRouteType.GTP;
        }

        this.headerLinkParams = {
            addon_type: this.headerLinkAddonType,
            intent: this.state.params.intent
        };

        // Initialize AddonsUtil with the current addon type, action and route type
        this.addonsUtil.addonType = this.addonType;
        this.addonsUtil.addonAction = this.intent;
        this.addonsUtil.addonRouteType = this.state.params.addon_type;

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (accountSummary: AccountSummary) => {
            this.lines = accountSummary.getLinesWithPlans;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsAddonPlans>(CmsAddonPlans), (result: CmsAddonPlans) => {
            this.planData = result.plans.find((plan: CmsAddonPlan) => plan.gtp1B && plan.type === this.addonType);
            this.pageData = this.planData.content.reviewData;
            this.pageData.addRemovePass.header = this.getPassHeader();

            this.gtpSupportLinks = Array.isArray(this.pageData.supportLinks) && this.pageData.supportLinks.length > 0 && typeof this.pageData.supportLinks[0] !== 'string'
                ? this.pageData.supportLinks as CmsGtpSupportLink[]
                : [];
            this.mexCanSupportLinks = Array.isArray(this.pageData.supportLinks) && typeof this.pageData.supportLinks[0] === 'string'
                ? this.pageData.supportLinks as string[]
                : [];
            this.imagePrimary = {
                mobile: Cloudinary.generateMediaFromCms(this.pageData.iconPrimary)
            };
            this.pageData.features.forEach((feature: CmsReviewFeatureData) => {
                this.features.push({
                    description: feature.description,
                    imageOption: {
                        mobile: Cloudinary.generateMediaFromCms(feature.icon)
                    }
                });
            });
        }));

        if (!this.enableCbmNoLevel) {
            this.subscriptions.push(XmStoreUtil.subscribe(
                this.xmStore.find<GlobalTravelPass>(
                    !this.isBusinessUser && this.isSeg6 ? StoreAction.GET_GLOBAL_TRAVEL_PASS_PLAN_EIGHT : StoreAction.GET_GLOBAL_TRAVEL_PASS, GlobalTravelPass
                ),
                (gtp: GlobalTravelPass) => {

                    this.addonLines = gtp.tabs.reduce((acc: {}, tab: GlobalTravelPassTab) => {
                        acc[tab.pass] = {
                            lines: tab.lines.map((line: GlobalTravelPassLine) => ({
                                line_id: line.lineIdKey,
                                displayName: line.nickname,
                                lineNumber: line.phoneNumber,
                                status: line.status
                            }))
                        };

                        return acc;
                    }, {});
                    // Update the addonLines in the AddonsUtil service
                    this.addonsUtil.addonLines = this.addonLines;
                    this.isLoading = false;
                }, () => {
                    this.isLoading = false;
                }
            ));
        } else {
            this.isLoading = false;
        }

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsMyAccountModals>(CmsMyAccountModals), (cms: CmsMyAccountModals) => {
            this.payAsYouGoModalData = cms.modals.payAsYouGoModal;
        }));
    }

    public getAddonLines(): AddOnPassOption[] {
        if (this.isGTP && this.addonLines?.gtp?.lines?.length > 0) {
            return this.addonLines.gtp.lines;
        } else if (this.isMexCan && this.addonLines?.can_mex?.lines?.length > 0) {
            return this.addonLines.can_mex.lines;
        }

        return [];
    }

    public onCancel(): void {
        this.state.go(this.pageData.secondaryLinkUiSref, this.state.params);
    }

    public onClickPrimary(): void {
        this.state.go(this.pageData.primaryBtnUiSref, this.state.params);
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public launchPayAsYouGoModal(supportLink: CmsGtpSupportLink): void {
        if (supportLink.isModal) {
            this.accountModal.open(XmPayAsYouGoModal, this.payAsYouGoModalData);
        }
    }

    /**
     * Handles changes to the selected passes in the UI.
     *
     * Updates the internal state based on the user's selection of passes,
     * sets the appropriate action (add or remove) for the passes,
     * enables or disables the "Next" button depending on whether any passes are selected,
     * and updates the payload for posting add-on actions.
     *
     * @param selectedPasses - An array of selected pass identifiers.
     */
    public handlePassSelectionChange(selectedPasses: string[]): void {
        const passSelection: AddRemovePassAction = {
            action: this.intent === 'add' ? AddonAction.ADD : AddonAction.REMOVE,
            lineIdKeys: selectedPasses,
            addOnType: this.addonType
        };
        if (selectedPasses.length > 0) {
            this.enableNextButton = true;
        } else {
            this.enableNextButton = false;
        }
        this.addonsUtil.postAddOnsPayload = passSelection;
    }

    /**
     * Returns the appropriate header string based on the current intent.
     *
     * If the intent is 'add', it returns the addHeader from pageData.addRemovePass.
     * If the intent is 'remove', it returns the manageHeader from pageData.addRemovePass.
     * If the intent is neither, it returns an empty string.
     *
     * @returns {string} The header string corresponding to the current intent.
     */
    private getPassHeader(): string {
        if (this.intent === 'add') {
            return this.pageData.addRemovePass.addHeader;
        } else if (this.intent === 'remove') {
            return this.pageData.addRemovePass.manageHeader;
        }

        return '';
    }
}
